import type { DragEvent, HTMLAttributes } from 'react'
import type { FsEntry, ThumbnailTicket } from '../api'
import EntryIcon from './EntryIcon'
import RowMenu, { type RowAction } from './RowMenu'

/**
 * 같은 폴더를 타일로 보는 화면.
 *
 * 사진 폴더에서는 이름 목록이 아무것도 말해주지 않는다 — `IMG_4821.JPG` 를 읽고 무엇인지
 * 아는 사람은 없다. 그래서 사진은 사진으로 보여준다.
 *
 * **그림만 걸러 보여주지는 않는다.** 사진첩이 아니라 파일 목록이라, 여기서 안 보이는 것이
 * 있으면 그것은 없는 것이 된다. 폴더도 문서도 제 타일을 갖고, 그림이 아닌 것은 아이콘을
 * 단다.
 *
 * 하는 일(열기, 고르기, 끌기, 행 메뉴)은 전부 목록 쪽에서 그대로 건네받는다. 보기가 둘로
 * 갈렸다고 동작이 둘로 갈리면, 다음에 고칠 때 한쪽만 고쳐진다.
 */
export default function EntryGrid({
  entries,
  ticket,
  selected,
  onToggle,
  onOpen,
  actionsFor,
  draggable,
  onDragStart,
  onDragEnd,
  dropTarget,
  pathOf,
  dropZone,
}: {
  entries: FsEntry[]
  ticket: ThumbnailTicket | null
  selected: Set<string>
  onToggle: (name: string) => void
  onOpen: (entry: FsEntry) => void
  actionsFor: (entry: FsEntry) => RowAction[]
  draggable: boolean
  onDragStart: (entry: FsEntry, event: DragEvent<HTMLElement>) => void
  onDragEnd: () => void
  /** 지금 무엇 위에 떠 있는지, 경로로. */
  dropTarget: string | null
  pathOf: (entry: FsEntry) => string
  /** 폴더 타일이 받아주게 하는 것들. 목록의 폴더 행이 쓰는 그것과 같다. */
  dropZone: (path: string) => HTMLAttributes<HTMLElement>
}) {
  return (
    <ul className="tiles">
      {entries.map((entry) => (
        <li
          key={entry.name}
          className={[
            'tile',
            selected.has(entry.name) ? 'selected' : '',
            dropTarget === pathOf(entry) ? 'drop-target' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          draggable={draggable}
          onDragStart={(event) => onDragStart(entry, event)}
          onDragEnd={onDragEnd}
          onDoubleClick={() => onOpen(entry)}
          {...(entry.type === 'dir' ? dropZone(pathOf(entry)) : {})}
        >
          <input
            type="checkbox"
            className="tile-check"
            aria-label={`${entry.name} 선택`}
            checked={selected.has(entry.name)}
            onChange={() => onToggle(entry.name)}
          />

          <span className="tile-menu">
            <RowMenu label={entry.name} actions={actionsFor(entry)} />
          </span>

          {/* 한 번 눌러 연다. 목록에서는 이름을 누르는 자리가 그것인데, 타일에서는
              그림 전체가 그 자리다. */}
          <button type="button" className="tile-face" onClick={() => onOpen(entry)}>
            <EntryIcon entry={entry} ticket={ticket} size="tile" />
          </button>

          <span className="tile-name" title={entry.name}>
            {entry.name}
          </span>
        </li>
      ))}
    </ul>
  )
}
